import { ReactNode, useEffect, useState } from "react";

import { AdjustmentsHorizontalIcon, ChevronDownIcon, ChevronUpIcon, LockClosedIcon, XCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";

export interface ITableFilterPill {
  key: string;
  label: string;
  onClear: () => void;
}

interface ITableFilterPanelProps {
  activeFilterPills: ITableFilterPill[];
  children: ReactNode;
  disabled?: boolean;
  defaultOpen?: boolean;
  defaultFiltersStorageKey?: string;
  getDefaultFilters?: () => unknown;
  onApplyDefaultFilters?: (filters: any) => void;
}

export default function TableFilterPanel({
  activeFilterPills,
  children,
  disabled = false,
  defaultOpen = true,
  defaultFiltersStorageKey,
  getDefaultFilters,
  onApplyDefaultFilters,
}: ITableFilterPanelProps) {
  const { t } = useTranslation("common");
  const [filtersOpen, setFiltersOpen] = useState(defaultOpen);
  const [hasSavedDefaultFilters, setHasSavedDefaultFilters] = useState(false);

  useEffect(() => {
    if (!defaultFiltersStorageKey || !onApplyDefaultFilters) return;

    const savedFilters = localStorage.getItem(defaultFiltersStorageKey);
    setHasSavedDefaultFilters(Boolean(savedFilters));

    if (!savedFilters) return;

    try {
      onApplyDefaultFilters(JSON.parse(savedFilters));
    } catch {
      localStorage.removeItem(defaultFiltersStorageKey);
      setHasSavedDefaultFilters(false);
    }
  }, [defaultFiltersStorageKey, onApplyDefaultFilters]);

  const handleSaveDefaultFilters = () => {
    if (!defaultFiltersStorageKey || !getDefaultFilters) return;

    localStorage.setItem(defaultFiltersStorageKey, JSON.stringify(getDefaultFilters()));
    setHasSavedDefaultFilters(true);
  };

  const handleClearDefaultFilters = () => {
    if (!defaultFiltersStorageKey) return;

    localStorage.removeItem(defaultFiltersStorageKey);
    setHasSavedDefaultFilters(false);
  };

  const showDefaultFilterActions = Boolean(defaultFiltersStorageKey && getDefaultFilters && onApplyDefaultFilters);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60">
      <div className="flex justify-between">
        <div className="flex w-full gap-3 px-3 py-2 transition-colors hover:bg-slate-100/70">
          <button type="button" onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen} className="flex min-w-0 items-center gap-2 text-left">
            <AdjustmentsHorizontalIcon className="h-4 w-4 flex-none text-slate-500" aria-hidden="true" />
            <span className="flex-none text-xs font-semibold uppercase tracking-wide text-slate-600">{t("Filters")}</span>
            {activeFilterPills.length === 0 && !filtersOpen && <span className="text-[11px] font-medium text-slate-400">{t("No filters applied")}</span>}
          </button>
          {activeFilterPills.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {activeFilterPills.map((pill) => (
                <span key={pill.key} className="inline-flex items-center gap-1 rounded-full bg-wilma-100 px-2 py-0.5 text-[11px] font-semibold text-wilma-700">
                  {pill.label}
                  <button
                    type="button"
                    onClick={pill.onClear}
                    disabled={disabled}
                    className="rounded-full p-0.5 text-wilma-600 transition-colors hover:bg-wilma-200 hover:text-wilma-800 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={t("Clear {{label}} filter", { label: pill.label })}
                  >
                    <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-none items-center gap-1 pr-2">
          {showDefaultFilterActions && (
            <>
              <button
                type="button"
                onClick={handleSaveDefaultFilters}
                disabled={disabled}
                title={t("Save these filters as default")}
                className={classNames(
                  "rounded p-1 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700",
                  hasSavedDefaultFilters ? "text-primary-600" : "",
                  disabled ? "cursor-not-allowed opacity-50" : ""
                )}
              >
                <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={handleClearDefaultFilters}
                disabled={disabled || !hasSavedDefaultFilters}
                title={t("Clear saved default filters")}
                className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <XCircleIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}
          <button type="button" onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}>
          {filtersOpen ? <ChevronUpIcon className="h-4 w-4 text-slate-500" aria-hidden="true" /> : <ChevronDownIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {filtersOpen && (
        <div className={classNames("px-3 pb-3 pt-1")}>
          {children}
        </div>
      )}
    </div>
  );
}
